import { registerWithAction } from './api';
import { showLoader, hideLoader } from '../../modules/loaders';

/**
 * Posting the registration. Nothing more than that.
 *
 * The form's own action is the endpoint, which is how one module serves every door:
 * the article page, the join wizard and the plans modal all post different routes
 * with the same body, and Blade already knows which. Nothing is assembled here —
 * the form is serialized whole, so a field added to RegistrationSpec travels
 * without a line of JavaScript changing.
 *
 * The answer goes back unread, because the doors do not agree on what it even
 * contains: a chapter gets a status to act on, an article gets none since nobody
 * decides anything there, and a signed-in join gets one but has no address left to
 * confirm. Reading it here would mean inventing a meaning for each silence.
 */
export function submitRegistration({ form, translate, onAccepted, onFailed } = {}) {
    const $form = $(form);

    const say = translate || ((key, fallback) => fallback);

    /**
     * The whole answer goes back, not just the sentence in it. A refusal can carry a
     * status and no message — RegistrationApprovalService answers a closed event,
     * a members-only event and a used guest place that way — and a door that wants
     * to say something better than "registration failed" needs to see which it was.
     */
    function failed(response) {
        if (onFailed) {
            const message = (response && response.message)
                || say('registration_failed_text', 'Registration failed.');

            onFailed(message, response || {});
        }
    }

    function accepted(response) {
        if (! response || ! response.success) {
            failed(response);
            return;
        }

        if (onAccepted) {
            onAccepted(response);
        }
    }

    // The request on its way, if any. A second click before it answers would post the
    // same account again, and that answer, arriving after the first signed the person
    // in, carries the session they had before and signs them out.
    let sending = null;

    return {
        /** True when the browser is happy with the fields, per core/validation. */
        valid() {
            if (! $form.length) {
                return false;
            }

            return typeof $form.valid === 'function' ? $form.valid() : true;
        },

        send() {
            if (sending) {
                return sending;
            }

            showLoader();

            sending = registerWithAction($form.attr('action'), $form.serialize())
                .done(accepted)
                .fail((jqXHR) => failed(jqXHR && jqXHR.responseJSON))
                .always(() => {
                    hideLoader();
                    sending = null;
                });

            return sending;
        }
    };
}
